import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/icons'
import Text from '../../elements/Typo/Text'
import { ExperimentDetailsI } from '../../_config/types'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { ErrorResponse, SuccessApiResponse } from '../../store/api/apiTypes'
import toast from 'react-hot-toast'
import { Tooltip } from 'react-daisyui'

interface CaptureDecisionModalProps {
    _ref: any
    experiment: ExperimentDetailsI
    onSave?: () => void
}

interface DecisionOption {
    code: string
    description: string
    description_text: string
}

const validateDecisionForm = (selectedDecision: string, notes: string, markAsTopLearning: boolean, learning: string): { type: 'toast' | 'field'; field?: 'notes' | 'learning'; message: string } | null => {
    if (!selectedDecision) return { type: 'toast', message: 'Please select a decision' }
    if (!notes.trim()) return { type: 'field', field: 'notes', message: 'Details of Business Decision is required' }
    if (notes.trim().length < 70) return { type: 'field', field: 'notes', message: 'Please provide a detailed explanation of the decision. (minimum 70 characters)' }
    if (notes.length > 1000) return { type: 'field', field: 'notes', message: 'Details cannot exceed 1000 characters' }
    if (markAsTopLearning && !learning.trim()) return { type: 'field', field: 'learning', message: 'Learning is required' }
    if (markAsTopLearning && learning.trim().length < 70) return { type: 'field', field: 'learning', message: 'Please provide detailed learning. (minimum 70 characters)' }
    if (markAsTopLearning && learning.length > 1000) return { type: 'field', field: 'learning', message: 'Learning cannot exceed 1000 characters' }
    return null
}

const extractPrefillData = (experiment: any) => {
    const result = { selectedDecision: '', notes: '', markAsTopLearning: false, learning: '' }
    if (experiment?.experiment_decisions?.length > 0) {
        const latestDecision = experiment.experiment_decisions[0]
        result.selectedDecision = latestDecision.decision?.code || ''
        result.notes = latestDecision.notes || ''
    }
    if (experiment?.is_top_learnings !== undefined) {
        result.markAsTopLearning = experiment.is_top_learnings
    }
    if (experiment?.learning_text) {
        result.learning = experiment.learning_text
    }
    return result
}

const CaptureDecisionModal: React.FC<CaptureDecisionModalProps> = ({ _ref, experiment, onSave }) => {
    const { t } = useTranslation()
    const { getExperimentMasterDecision, saveExperimentDecision, getExperimentDetails } = useExperimentApi()

    const [decisionTypes, setDecisionTypes] = useState<DecisionOption[]>([])
    const [selectedDecision, setSelectedDecision] = useState<string>('')
    const [notes, setNotes] = useState<string>('')
    const [markAsTopLearning, setMarkAsTopLearning] = useState<boolean>(false)
    const [learning, setLearning] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)
    const [notesError, setNotesError] = useState<string>('')
    const [learningError, setLearningError] = useState<string>('')
    const [experimentData, setExperimentData] = useState<ExperimentDetailsI>(experiment)

    const kpiOptions = experimentData?.experiment_metadatas?.metrices || []

    const _handleFetchDecisionTypes = (exp?: ExperimentDetailsI) => {
        const currentExp = exp || experimentData
        setLoading(true)
        getExperimentMasterDecision(
            { literal: currentExp.experiment_literal },
            (resp: SuccessApiResponse<DecisionOption[]>) => {
                setDecisionTypes(resp.data || [])
                setLoading(false)
            },
            (error: ErrorResponse<DecisionOption[]>) => {
                setLoading(false)
                toast.error(error.message || t('Failed to load decision types'))
            },
        )
    }

    const _handleDecisionSelect = (decisionId: string) => {
        setSelectedDecision(decisionId)
    }

    const _handleSave = () => {
        const validationError = validateDecisionForm(selectedDecision, notes, markAsTopLearning, learning)
        if (validationError) {
            if (validationError.type === 'toast') {
                toast.error(t(validationError.message))
            } else if (validationError.field === 'learning') {
                setLearningError(t(validationError.message))
            } else {
                setNotesError(t(validationError.message))
            }
            return
        }

        setSaving(true)
        saveExperimentDecision(
            {
                experiment_literal: experimentData.experiment_literal,
                decision_code: selectedDecision,
                notes,
                other_decision: '',
                mark_as_top_learning: markAsTopLearning,
                learning_text: markAsTopLearning ? learning : '',
            },
            (resp: SuccessApiResponse<any>) => {
                setSaving(false)
                toast.success(resp?.message ? t(resp.message) : t('Decision saved successfully'))
                onSave?.()
                _handleClose()
            },
            (error: ErrorResponse<any>) => {
                setSaving(false)
                toast.error(error?.message ? t(error.message) : t('Failed to save decision'))
            },
        )
    }

    const _handleClose = () => {
        _ref.current?.close()
        setNotesError('')
        setLearningError('')
    }

    const _prefillForm = (exp?: ExperimentDetailsI) => {
        const currentExp = exp || experimentData
        setNotesError('')
        setLearningError('')
        const data = extractPrefillData(currentExp)
        setSelectedDecision(data.selectedDecision)
        setNotes(data.notes)
        setMarkAsTopLearning(data.markAsTopLearning)
        setLearning(data.learning)
    }

    useEffect(() => {
        if (experiment?.experiment_literal) {
            getExperimentDetails(
                { literal: experiment.experiment_literal, use_orm: true },
                (resp: SuccessApiResponse<ExperimentDetailsI>) => {
                    setExperimentData(resp.data)
                    _handleFetchDecisionTypes(resp.data)
                    _prefillForm(resp.data)
                },
                (error: ErrorResponse<ExperimentDetailsI>) => {},
            )
        }
    }, [experiment?.experiment_literal])

    useEffect(() => {
        const dialog = _ref?.current
        if (!dialog) return

        const observer = new MutationObserver((mutations) => {
            const wasOpened = mutations.some((m) => m.attributeName === 'open' && dialog.hasAttribute('open'))
            if (wasOpened && experiment?.experiment_literal) {
                getExperimentDetails(
                    { literal: experiment.experiment_literal, use_orm: true },
                    (resp: SuccessApiResponse<ExperimentDetailsI>) => {
                        setExperimentData(resp.data)
                        _handleFetchDecisionTypes(resp.data)
                        _prefillForm(resp.data)
                    },
                    (error: ErrorResponse<ExperimentDetailsI>) => {},
                )
            }
        })

        observer.observe(dialog, { attributes: true })
        return () => observer.disconnect()
    }, [_ref, experiment?.experiment_literal])

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box rounded-[0.833rem] w-lg max-w-xl flex flex-col gap-4 p-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <Text size="xl" className="font-bold">
                        {t('Business Decision Made')}
                    </Text>
                    <button type="button" onClick={_handleClose} className="btn btn-sm btn-circle btn-ghost">
                        <Icon name="close" size={1} />
                    </button>
                </div>

                {loading && (
                    <div className="flex justify-center items-center py-8">
                        <span className="loading loading-spinner loading-md"></span>
                    </div>
                )}

                {!loading && (
                    <>
                        {/* Two Column Layout: Decision Taken and Impact KPI(s) */}
                        <div className="grid grid-cols-2 gap-6">
                            {/* Decision Taken Section */}
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M13.3333 4L6 11.3333L2.66666 8" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <Text size="sm" className="font-semibold">
                                        {t('Decision Taken')}
                                    </Text>
                                    <Tooltip message={t('Select the business decision that was made')} color="info" className="text-start transition-opacity delay-300 duration-200" content="" position={'right'}>
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-help" role="img" aria-label={t('Select the business decision that was made')}>
                                            <circle cx="8" cy="8" r="7" stroke="#9CA3AF" strokeWidth="1.5" />
                                            <path d="M8 12V8M8 5V4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </Tooltip>
                                </div>
                                <div className="flex flex-col gap-0.5 pl-1">
                                    {decisionTypes.map((decision) => (
                                        <button type="button" key={decision.code} onClick={() => _handleDecisionSelect(decision.code)} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-0 rounded transition-colors text-left">
                                            <div className={`w-2.5 h-2.5 rounded-full flex items-center justify-center border-2 transition-all ${selectedDecision === decision.code ? 'border-[#FFC107] bg-[#FFC107]' : 'border-gray-300'}`}>
                                                {selectedDecision === decision.code && <div className="w-1.5 h-1.5 rounded-full bg-[#FFC107]"></div>}
                                            </div>
                                            <Text size="sm" className="font-normal">
                                                {decision.description}
                                            </Text>

                                            <Tooltip message={decision.description_text} color="info" className="text-start transition-opacity delay-300 duration-200" content="" position={'right'}>
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-help" role="img" aria-label={decision.description_text}>
                                                    <circle cx="8" cy="8" r="7" stroke="#9CA3AF" strokeWidth="1.5" />
                                                    <path d="M8 12V8M8 5V4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                            </Tooltip>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Impact KPI(s) Section */}
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M13.3333 4L6 11.3333L2.66666 8" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <Text size="sm" className="font-semibold">
                                        {t('Impact KPI(s)')}
                                    </Text>
                                </div>
                                <div className="flex flex-wrap gap-2 bg-[#F9FAFB] p-1 rounded-md border border-gray-200">
                                    {kpiOptions.length === 0 ? (
                                        <Text size="xs" className="text-gray-400">
                                            {t('No KPIs defined for this experiment.')}
                                        </Text>
                                    ) : (
                                        kpiOptions.map((kpi: any) => (
                                            <div key={kpi.kpi_literal} className="flex items-center leading-[1.2] gap-2 px-1 py-0.5 bg-white rounded-[0.2rem] border border-gray-300 text-sm h-fit opacity-70 cursor-not-allowed">
                                                <Text size="xs" className="font-medium">
                                                    {kpi.literral_value}
                                                </Text>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Notes Section */}
                        <div className="flex flex-col gap-3 pt-2">
                            <div className="flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M13.3333 4L6 11.3333L2.66666 8" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <Text size="sm" className="font-semibold">
                                    {t('Details of Business Decision')}
                                </Text>
                                <Text size="xs" className="text-red-500">
                                    *
                                </Text>
                            </div>
                            <div className="flex flex-col gap-1">
                                <textarea
                                    className={`border bg-amber-50 bg-opacity-60 w-full h-16 text-[0.55rem] resize-none focus:outline-none leading-[1.2] focus:ring-2 rounded-[0.3rem] p-2 ${notesError ? 'border-red-500 focus:ring-red-300' : 'border-slate-300 focus:ring-slate-300'}`}
                                    placeholder={t('Write your custom note if you have any suggestion')}
                                    value={notes}
                                    maxLength={1000}
                                    onChange={(e) => {
                                        setNotes(e.target.value)
                                        setNotesError('')
                                    }}
                                />
                                <div className="flex justify-between items-center">
                                    {notesError && (
                                        <Text size="xs" className="text-red-500">
                                            {notesError}
                                        </Text>
                                    )}
                                    <Text size="xs" className={`ml-auto ${notes.length > 1000 ? 'text-red-500' : 'text-gray-500'}`}>
                                        {notes.length}/1000
                                    </Text>
                                </div>
                            </div>
                        </div>

                        {/* Mark As Top Learning */}
                        <div className="flex flex-col gap-3">
                            <div>
                                <Tooltip message={t('Select a Business Decision to mark this experiment as a Top Learning.')} color="info" position="top" className="whitespace-normal break-normal">
                                    <button
                                        type="button"
                                        className={`flex items-center gap-2 text-left ${selectedDecision ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                                        disabled={!selectedDecision}
                                        onClick={() => {
                                            setMarkAsTopLearning(!markAsTopLearning)
                                            if (markAsTopLearning) {
                                                setLearning('')
                                            }
                                        }}
                                    >
                                        <div className={`w-[1.125rem] h-[1.125rem] rounded-md shadow-sm flex justify-center items-center border ${markAsTopLearning ? 'bg-primary' : 'bg-white'}`}>
                                            <Icon name="bulb" size="1rem" color="#565454" />
                                        </div>

                                        <Text size="sm" className="font-semibold">
                                            {t('Mark As Top Learning')}
                                        </Text>
                                    </button>
                                </Tooltip>
                            </div>

                            {/* Learning Text Area - Conditional */}
                            {markAsTopLearning && (
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <Text size="sm" className="font-semibold">
                                            {t('Learning')}
                                        </Text>
                                        <Text size="xs" className="text-red-500">
                                            *
                                        </Text>
                                    </div>
                                    <textarea
                                        className={`border bg-amber-50 bg-opacity-60 w-full h-16 text-[0.55rem] resize-none focus:outline-none leading-[1.2] focus:ring-2 rounded-[0.3rem] p-2 ${learningError ? 'border-red-500 focus:ring-red-300' : 'border-slate-300 focus:ring-slate-300'}`}
                                        placeholder={t('Enter the learning details')}
                                        value={learning}
                                        maxLength={1000}
                                        onChange={(e) => {
                                            setLearning(e.target.value)
                                            setLearningError('')
                                        }}
                                    />
                                    <div className="flex justify-between items-center">
                                        {learningError && (
                                            <Text size="xs" className="text-red-500">
                                                {learningError}
                                            </Text>
                                        )}
                                        <Text size="xs" className={`ml-auto ${learning.length > 1000 ? 'text-red-500' : 'text-gray-500'}`}>
                                            {learning.length}/1000
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-center pt-4">
                            <button
                                type="button"
                                className={`flex items-center gap-2 bg-black text-white hover:bg-gray-800 rounded-full px-10 py-2.5 transition-colors ${saving || !selectedDecision ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={_handleSave}
                                disabled={saving || !selectedDecision}
                            >
                                {saving ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path d="M13.3333 4L6 11.3333L2.66666 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span className="font-medium">{t('Save')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={_handleClose}>
                    close
                </button>
            </form>
        </dialog>
    )
}

export default CaptureDecisionModal
